// /app/frontend/src/components/admin/MeasurementProjectsTab.jsx
// Measurement Projects - Tabbed interface for window treatment measurement workflow
// With offline support for field installers

import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { 
  Plus, Search, Trash2, Save, X, Loader2, Camera, Mic, MicOff,
  ChevronRight, ChevronLeft, Check, Eye, Pencil, FolderOpen,
  Home, Ruler, Settings, Palette, Square, FileText, Play, Pause,
  Image as ImageIcon, Upload, Sparkles, Volume2, HelpCircle, 
  CreditCard, Receipt, Smartphone, BookOpen, ChevronDown, ChevronUp, Info,
  Wifi, WifiOff, Cloud, CloudOff, RefreshCw, AlertCircle, Calendar, User, HardHat,
  GraduationCap
} from 'lucide-react';
import { useOfflineSync } from '../../hooks/useOfflineSync';
import { BestPhotosTrainingMode } from './BestPhotosTrainingMode';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Tab configuration
const PROJECT_TABS = [
  { id: 'project', label: 'Project Info', icon: Home },
  { id: 'window', label: 'Window & Photos', icon: Camera },
  { id: 'measurements', label: 'Measurements', icon: Ruler },
  { id: 'details', label: 'Installation', icon: Settings },
  { id: 'product', label: 'Product', icon: Palette },
  { id: 'fascia', label: 'Fascia', icon: Square },
  { id: 'review', label: 'Review', icon: FileText },
];

const MeasurementProjectsTab = ({ token }) => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedProject, setSelectedProject] = useState(null);
  const [showEditor, setShowEditor] = useState(false);
  const [activeTab, setActiveTab] = useState('project');
  const [selectedWindowId, setSelectedWindowId] = useState(null);
  const [options, setOptions] = useState({});
  const [saving, setSaving] = useState(false);
  const [showTrainingMode, setShowTrainingMode] = useState(false);

  // Offline sync hook
  const {
    isOnline,
    pendingCount,
    isSyncing,
    lastSyncTime,
    syncAll,
    createProjectOffline,
    fetchMergedProjects,
    cacheOptionsData,
    getCachedOptions,
  } = useOfflineSync(token);

  // Fetch projects and options on mount
  useEffect(() => {
    fetchProjects();
    fetchOptions();
  }, []);

  // Re-fetch when coming back online
  useEffect(() => {
    if (isOnline) {
      fetchProjects();
    }
  }, [isOnline]);

  const fetchProjects = async () => {
    setLoading(true);
    try {
      // Use merged projects (online + offline)
      const mergedProjects = await fetchMergedProjects();
      setProjects(mergedProjects);
    } catch (error) {
      // Fallback to API if hook fails
      try {
        const response = await axios.get(`${API_URL}/api/measurement-projects`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setProjects(response.data);
      } catch (apiError) {
        toast.error('Failed to load projects');
      }
    }
    setLoading(false);
  };

  const fetchOptions = async () => {
    try {
      // Try to get cached options first (for offline use)
      const cachedOptions = await getCachedOptions();
      if (cachedOptions) {
        setOptions(cachedOptions);
      }

      // Fetch from API if online
      if (isOnline) {
        const response = await axios.get(`${API_URL}/api/measurement-projects/options`);
        setOptions(response.data);
        // Cache for offline use
        await cacheOptionsData(response.data);
      }
    } catch (error) {
      console.error('Failed to load options:', error);
    }
  };

  const createProject = async () => {
    try {
      const result = await createProjectOffline({
        project_name: 'New Measurement Project',
        room_type: 'Living Room'
      });

      if (result.success) {
        setSelectedProject(result.data);
        setActiveTab('project');
        setShowEditor(true);
        fetchProjects();
        
        if (result.offline) {
          toast.success('Project created (offline mode - will sync when online)');
        } else {
          toast.success('Project created');
        }
      }
    } catch (error) {
      toast.error('Failed to create project');
    }
  };

  const handleSync = async () => {
    const result = await syncAll();
    if (result.success) {
      toast.success(result.message);
      fetchProjects();
    } else {
      toast.error(result.message || 'Sync failed');
    }
  };

  const openProject = (project) => {
    setSelectedProject(project);
    setSelectedWindowId(project.windows?.[0]?.id || null);
    setActiveTab('project');
    setShowEditor(true);
  };

  const deleteProject = async (projectId) => {
    if (!window.confirm('Delete this project? This cannot be undone.')) return;
    try {
      await axios.delete(`${API_URL}/api/measurement-projects/${projectId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProjects(prev => prev.filter(p => p.id !== projectId));
      toast.success('Project deleted');
    } catch (error) {
      toast.error('Failed to delete project');
    }
  };

  const filteredProjects = projects.filter(p => 
    !searchQuery || 
    p.project_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    p.customer_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    p.room_type?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="p-6" data-testid="measurement-projects-tab">
      {/* Offline Status Banner */}
      {!isOnline && (
        <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-center justify-between" data-testid="offline-banner">
          <div className="flex items-center gap-2">
            <WifiOff className="w-5 h-5 text-amber-600" />
            <div>
              <p className="font-medium text-amber-800">You're offline</p>
              <p className="text-sm text-amber-600">Changes will be saved locally and synced when you're back online</p>
            </div>
          </div>
          <CloudOff className="w-6 h-6 text-amber-400" />
        </div>
      )}

      {/* Pending Sync Banner */}
      {isOnline && pendingCount > 0 && (
        <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg flex items-center justify-between" data-testid="pending-sync-banner">
          <div className="flex items-center gap-2">
            <Cloud className="w-5 h-5 text-blue-600" />
            <div>
              <p className="font-medium text-blue-800">{pendingCount} item(s) pending sync</p>
              <p className="text-sm text-blue-600">Click sync to upload your offline changes</p>
            </div>
          </div>
          <Button 
            onClick={handleSync} 
            disabled={isSyncing}
            className="bg-blue-600 hover:bg-blue-700"
            data-testid="sync-btn"
          >
            {isSyncing ? (
              <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Syncing...</>
            ) : (
              <><RefreshCw className="w-4 h-4 mr-2" /> Sync Now</>
            )}
          </Button>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            Measurement Projects
            {isOnline ? (
              <Wifi className="w-5 h-5 text-green-500" title="Online" />
            ) : (
              <WifiOff className="w-5 h-5 text-amber-500" title="Offline" />
            )}
          </h2>
          <p className="text-neutral-500">Create and manage window measurement projects</p>
        </div>
        <div className="flex items-center gap-2">
          <Button 
            onClick={() => setShowTrainingMode(true)} 
            variant="outline"
            className="border-blue-200 text-blue-600 hover:bg-blue-50"
          >
            <GraduationCap className="w-4 h-4 mr-2" /> Photo Training
          </Button>
          <Button onClick={createProject} className="bg-amber-600 hover:bg-amber-700" data-testid="new-project-btn">
            <Plus className="w-4 h-4 mr-2" /> New Project
          </Button>
        </div>
      </div>

      {/* Search */}
      <div className="relative mb-6 max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
        <Input
          placeholder="Search projects..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-9"
          data-testid="project-search"
        />
      </div>

      {/* Projects Grid */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-neutral-400" />
        </div>
      ) : filteredProjects.length === 0 ? (
        <div className="text-center py-16 bg-neutral-50 rounded-lg border border-dashed">
          <Ruler className="w-12 h-12 mx-auto mb-4 text-neutral-300" />
          <h3 className="text-lg font-medium text-neutral-600">No measurement projects yet</h3>
          <p className="text-neutral-500 mb-4">Create your first project to get started</p>
          <Button onClick={createProject} variant="outline">
            <Plus className="w-4 h-4 mr-2" /> Create Project
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredProjects.map(project => (
            <ProjectCard
              key={project.id}
              project={project}
              onOpen={() => openProject(project)}
              onDelete={() => deleteProject(project.id)}
            />
          ))}
        </div>
      )}

      {/* Project Editor Modal */}
      {showEditor && selectedProject && (
        <ProjectEditor
          project={selectedProject}
          setProject={setSelectedProject}
          options={options}
          token={token}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          selectedWindowId={selectedWindowId}
          setSelectedWindowId={setSelectedWindowId}
          onClose={() => { setShowEditor(false); fetchProjects(); }}
          onSave={fetchProjects}
          onProjectLinked={fetchProjects}
        />
      )}

      {/* Best Photos Training Mode - Placed in main MeasurementProjectsTab */}
      <BestPhotosTrainingMode 
        isOpen={showTrainingMode} 
        onClose={() => setShowTrainingMode(false)} 
        token={token}
      />
    </div>
  );
};

// Project Card Component
const ProjectCard = ({ project, onOpen, onDelete }) => {
  const statusColors = {
    draft: 'bg-neutral-100 text-neutral-700',
    in_progress: 'bg-blue-100 text-blue-700',
    completed: 'bg-green-100 text-green-700',
    submitted: 'bg-purple-100 text-purple-700'
  };

  const isOffline = project._isOffline || project.id?.startsWith('temp_') || project.syncStatus === 'new' || project.syncStatus === 'modified';
  const isPendingSync = project.syncStatus === 'new' || project.syncStatus === 'modified';

  return (
    <div 
      className={`bg-white border rounded-lg p-4 hover:shadow-md transition-shadow cursor-pointer ${isPendingSync ? 'border-amber-300 border-2' : ''}`}
      onClick={onOpen}
      data-testid={`project-card-${project.id}`}
    >
      <div className="flex items-start justify-between mb-3">
        <div>
          <h3 className="font-semibold text-lg flex items-center gap-2">
            {project.project_name}
            {isPendingSync && (
              <span className="text-amber-500" title="Pending sync">
                <CloudOff className="w-4 h-4" />
              </span>
            )}
          </h3>
          <p className="text-sm text-neutral-500">{project.room_type}</p>
        </div>
        <div className="flex items-center gap-1">
          {isPendingSync && (
            <span className="px-2 py-1 rounded text-xs font-medium bg-amber-100 text-amber-700">
              {project.syncStatus === 'new' ? 'New (offline)' : 'Modified'}
            </span>
          )}
          <span className={`px-2 py-1 rounded text-xs font-medium ${statusColors[project.status] || statusColors.draft}`}>
            {project.status?.replace('_', ' ') || 'Draft'}
          </span>
        </div>
      </div>
      
      {project.customer_name && (
        <p className="text-sm text-neutral-600 mb-2">
          <span className="font-medium">Customer:</span> {project.customer_name}
        </p>
      )}
      
      <div className="flex items-center justify-between text-xs text-neutral-500 mt-4 pt-3 border-t">
        <span>{project.windows?.length || 0} window(s)</span>
        <span>{new Date(project.created_at).toLocaleDateString()}</span>
      </div>
      
      <div className="flex items-center gap-2 mt-3">
        <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); onOpen(); }}>
          <Pencil className="w-3 h-3 mr-1" /> Edit
        </Button>
        <Button size="sm" variant="ghost" className="text-red-500" onClick={(e) => { e.stopPropagation(); onDelete(); }}>
          <Trash2 className="w-3 h-3" />
        </Button>
      </div>
    </div>
  );
};

// Main Project Editor Component
const ProjectEditor = ({ 
  project, setProject, options, token, 
  activeTab, setActiveTab, 
  selectedWindowId, setSelectedWindowId,
  onClose, onSave,
  onProjectLinked
}) => {
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const selectedWindow = project.windows?.find(w => w.id === selectedWindowId);

  const updateProject = async (updates) => {
    try {
      const response = await axios.put(
        `${API_URL}/api/measurement-projects/${project.id}`,
        updates,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setProject(response.data);
    } catch (error) {
      toast.error('Failed to update project');
    }
  };

  const addWindow = async () => {
    try {
      const windowCount = (project.windows?.length || 0) + 1;
      const roomName = project.custom_room_name || project.room_type;
      const response = await axios.post(
        `${API_URL}/api/measurement-projects/${project.id}/windows`,
        { window_name: `${roomName} - Window ${windowCount}` },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setProject(prev => ({
        ...prev,
        windows: [...(prev.windows || []), response.data]
      }));
      setSelectedWindowId(response.data.id);
      toast.success('Window added');
    } catch (error) {
      toast.error('Failed to add window');
    }
  };

  const updateWindow = async (windowId, updates) => {
    try {
      await axios.put(
        `${API_URL}/api/measurement-projects/${project.id}/windows/${windowId}`,
        updates,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setProject(prev => ({
        ...prev,
        windows: prev.windows.map(w => w.id === windowId ? { ...w, ...updates } : w)
      }));
    } catch (error) {
      toast.error('Failed to update window');
    }
  };

  const deleteWindow = async (windowId) => {
    if (!window.confirm('Delete this window?')) return;
    try {
      await axios.delete(
        `${API_URL}/api/measurement-projects/${project.id}/windows/${windowId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setProject(prev => ({
        ...prev,
        windows: prev.windows.filter(w => w.id !== windowId)
      }));
      if (selectedWindowId === windowId) {
        setSelectedWindowId(project.windows?.find(w => w.id !== windowId)?.id || null);
      }
      toast.success('Window deleted');
    } catch (error) {
      toast.error('Failed to delete window');
    }
  };

  const handleSaveAndClose = async () => {
    setSaving(true);
    await updateProject({ status: project.status === 'draft' ? 'in_progress' : project.status });
    setSaving(false);
    onSave();
    onClose();
    toast.success('Project saved');
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-6xl max-h-[95vh] flex flex-col p-0 gap-0">
        {/* Header */}
        <DialogHeader className="p-4 border-b">
          <DialogTitle className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Ruler className="w-5 h-5 text-amber-600" />
              <span>{project.project_name}</span>
              <span className="text-sm font-normal text-neutral-500">• {project.room_type}</span>
            </div>
            <Button 
              onClick={handleSaveAndClose} 
              disabled={saving}
              className="bg-amber-600 hover:bg-amber-700"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
              Save & Close
            </Button>
          </DialogTitle>
        </DialogHeader>

        {/* Tab Navigation */}
        <div className="flex border-b bg-neutral-50 px-4 overflow-x-auto">
          {PROJECT_TABS.map(tab => {
            const TabIcon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-3 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
                  activeTab === tab.id
                    ? 'border-amber-500 text-amber-700 bg-white'
                    : 'border-transparent text-neutral-600 hover:text-neutral-900'
                }`}
                data-testid={`tab-${tab.id}`}
              >
                <TabIcon className="w-4 h-4" />
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* Tab Content */}
        <div className="flex-1 overflow-auto p-6">
          {activeTab === 'project' && (
            <ProjectInfoTab
              project={project}
              setProject={setProject}
              options={options}
              updateProject={updateProject}
            />
          )}
          
          {activeTab === 'window' && (
            <WindowPhotosTab
              project={project}
              selectedWindow={selectedWindow}
              selectedWindowId={selectedWindowId}
              setSelectedWindowId={setSelectedWindowId}
              addWindow={addWindow}
              updateWindow={updateWindow}
              deleteWindow={deleteWindow}
              token={token}
              setProject={setProject}
            />
          )}
          
          {activeTab === 'measurements' && (
            <MeasurementsTab
              project={project}
              selectedWindow={selectedWindow}
              updateWindow={updateWindow}
              token={token}
              setProject={setProject}
            />
          )}
          
          {activeTab === 'details' && (
            <InstallationTab
              selectedWindow={selectedWindow}
              updateWindow={updateWindow}
              options={options}
            />
          )}
          
          {activeTab === 'product' && (
            <ProductTab
              selectedWindow={selectedWindow}
              updateWindow={updateWindow}
              options={options}
            />
          )}
          
          {activeTab === 'fascia' && (
            <FasciaTab
              selectedWindow={selectedWindow}
              updateWindow={updateWindow}
              options={options}
            />
          )}
          
          {activeTab === 'review' && (
            <ReviewTab
              project={project}
              updateProject={updateProject}
              token={token}
              onProjectLinked={(projectId, projectNumber) => {
                setProject(prev => ({
                  ...prev,
                  linked_project_id: projectId,
                  linked_project_number: projectNumber,
                  status: 'submitted'
                }));
                if (onProjectLinked) onProjectLinked();
              }}
            />
          )}
        </div>

        {/* Footer Navigation */}
        <div className="flex items-center justify-between p-4 border-t bg-neutral-50">
          <Button
            variant="outline"
            onClick={() => {
              const currentIndex = PROJECT_TABS.findIndex(t => t.id === activeTab);
              if (currentIndex > 0) {
                setActiveTab(PROJECT_TABS[currentIndex - 1].id);
              }
            }}
            disabled={activeTab === PROJECT_TABS[0].id}
          >
            <ChevronLeft className="w-4 h-4 mr-1" /> Previous
          </Button>
          
          <div className="flex items-center gap-1">
            {PROJECT_TABS.map((tab, i) => (
              <div
                key={tab.id}
                className={`w-2 h-2 rounded-full ${
                  activeTab === tab.id ? 'bg-amber-500' : 'bg-neutral-300'
                }`}
              />
            ))}
          </div>
          
          <Button
            onClick={() => {
              const currentIndex = PROJECT_TABS.findIndex(t => t.id === activeTab);
              if (currentIndex < PROJECT_TABS.length - 1) {
                setActiveTab(PROJECT_TABS[currentIndex + 1].id);
              }
            }}
            disabled={activeTab === PROJECT_TABS[PROJECT_TABS.length - 1].id}
          >
            Next <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// ============== TAB COMPONENTS ==============

// Tab 1: Project Info
const ProjectInfoTab = ({ project, setProject, options, updateProject }) => {
  const [localData, setLocalData] = useState({
    project_name: project.project_name || '',
    room_type: project.room_type || '',
    custom_room_name: project.custom_room_name || '',
    customer_name: project.customer_name || '',
    customer_address: project.customer_address || '',
    customer_phone: project.customer_phone || '',
    customer_email: project.customer_email || ''
  });

  const handleBlur = (field) => {
    if (localData[field] !== project[field]) {
      updateProject({ [field]: localData[field] });
    }
  };

  return (
    <div className="space-y-6 max-w-2xl">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>Project Name *</Label>
          <Input
            value={localData.project_name}
            onChange={(e) => setLocalData(prev => ({ ...prev, project_name: e.target.value }))}
            onBlur={() => handleBlur('project_name')}
            placeholder="e.g., Smith Residence - Living Room"
            data-testid="project-name-input"
          />
        </div>
        <div>
          <Label>Room Type *</Label>
          <select
            value={localData.room_type}
            onChange={(e) => {
              setLocalData(prev => ({ ...prev, room_type: e.target.value }));
              updateProject({ room_type: e.target.value });
            }}
            className="w-full h-10 px-3 border rounded-md bg-white"
            data-testid="room-type-select"
          >
            {(options.room_types || []).map(room => (
              <option key={room} value={room}>{room}</option>
            ))}
          </select>
        </div>
      </div>

      {localData.room_type === 'Custom' && (
        <div>
          <Label>Custom Room Name</Label>
          <Input
            value={localData.custom_room_name}
            onChange={(e) => setLocalData(prev => ({ ...prev, custom_room_name: e.target.value }))}
            onBlur={() => handleBlur('custom_room_name')}
            placeholder="Enter custom room name"
          />
        </div>
      )}

      <div className="border-t pt-6">
        <h3 className="font-semibold mb-4">Customer Information</h3>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label>Customer Name</Label>
            <Input
              value={localData.customer_name}
              onChange={(e) => setLocalData(prev => ({ ...prev, customer_name: e.target.value }))}
              onBlur={() => handleBlur('customer_name')}
              placeholder="John Doe"
            />
          </div>
          <div>
            <Label>Phone</Label>
            <Input
              value={localData.customer_phone}
              onChange={(e) => setLocalData(prev => ({ ...prev, customer_phone: e.target.value }))}
              onBlur={() => handleBlur('customer_phone')}
              placeholder="(555) 123-4567"
            />
          </div>
          <div>
            <Label>Email</Label>
            <Input
              type="email"
              value={localData.customer_email}
              onChange={(e) => setLocalData(prev => ({ ...prev, customer_email: e.target.value }))}
              onBlur={() => handleBlur('customer_email')}
              placeholder="john@example.com"
            />
          </div>
          <div>
            <Label>Address</Label>
            <Input
              value={localData.customer_address}
              onChange={(e) => setLocalData(prev => ({ ...prev, customer_address: e.target.value }))}
              onBlur={() => handleBlur('customer_address')}
              placeholder="123 Main St, City, State"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

// Photo Guide Component - Collapsible help for taking measurement photos
const PhotoGuide = () => {
  const [isOpen, setIsOpen] = useState(false);
  
  return (
    <div className="mb-6 border border-blue-200 rounded-lg overflow-hidden bg-gradient-to-r from-blue-50 to-indigo-50">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-4 py-3 flex items-center justify-between text-left hover:bg-blue-100/50 transition-colors"
      >
        <div className="flex items-center gap-2">
          <BookOpen className="w-5 h-5 text-blue-600" />
          <span className="font-medium text-blue-900">How to Take Measurement Photos</span>
          <span className="text-xs bg-blue-200 text-blue-800 px-2 py-0.5 rounded-full">Guide</span>
        </div>
        {isOpen ? <ChevronUp className="w-5 h-5 text-blue-600" /> : <ChevronDown className="w-5 h-5 text-blue-600" />}
      </button>
      
      {isOpen && (
        <div className="px-4 pb-4 space-y-4">
          {/* Why Reference Object */}
          <div className="bg-white rounded-lg p-4 border border-blue-100">
            <h4 className="font-semibold text-blue-900 mb-2 flex items-center gap-2">
              <Info className="w-4 h-4" /> Why Use a Reference Object?
            </h4>
            <p className="text-sm text-neutral-700">
              The AI cannot determine actual window size from a photo alone. By placing a <strong>known-size object</strong> in 
              the frame, the AI uses it as a scale reference to calculate real dimensions.
            </p>
          </div>

          {/* Visual Diagram */}
          <div className="bg-white rounded-lg p-4 border border-blue-100">
            <h4 className="font-semibold text-blue-900 mb-3">Photo Setup Example</h4>
            <div className="flex gap-4">
              {/* Diagram */}
              <div className="flex-1 bg-neutral-100 rounded-lg p-4 font-mono text-xs">
                <div className="border-2 border-dashed border-neutral-400 rounded p-3 relative">
                  <div className="text-center text-neutral-500 mb-8">
                    ← WINDOW FRAME →
                  </div>
                  <div className="h-16 border border-neutral-300 rounded flex items-center justify-center text-neutral-400">
                    Window Glass
                  </div>
                  <div className="mt-3 flex items-end gap-2">
                    <div className="bg-amber-200 border border-amber-400 rounded px-2 py-1 text-amber-800 text-[10px]">
                      📏 RULER
                    </div>
                    <span className="text-neutral-500 text-[10px]">← Place on windowsill</span>
                  </div>
                </div>
              </div>
              
              {/* Tips */}
              <div className="flex-1 space-y-2">
                <div className="flex items-start gap-2 text-sm">
                  <Check className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                  <span>Capture the <strong>full window</strong> in frame</span>
                </div>
                <div className="flex items-start gap-2 text-sm">
                  <Check className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                  <span>Reference object must be <strong>clearly visible</strong></span>
                </div>
                <div className="flex items-start gap-2 text-sm">
                  <Check className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                  <span>Keep object <strong>flat & parallel</strong> to window</span>
                </div>
                <div className="flex items-start gap-2 text-sm">
                  <Check className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                  <span>Place near the <strong>window frame</strong> for accuracy</span>
                </div>
                <div className="flex items-start gap-2 text-sm text-red-600">
                  <X className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <span>Avoid blurry photos or angled reference objects</span>
                </div>
              </div>
            </div>
          </div>

          {/* Reference Objects Table */}
          <div className="bg-white rounded-lg p-4 border border-blue-100">
            <h4 className="font-semibold text-blue-900 mb-3">Accepted Reference Objects</h4>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              <div className="flex items-center gap-3 p-2 bg-neutral-50 rounded-lg">
                <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                  <Ruler className="w-5 h-5 text-amber-700" />
                </div>
                <div>
                  <p className="font-medium text-sm">12" Ruler</p>
                  <p className="text-xs text-neutral-500">Best choice</p>
                </div>
              </div>
              <div className="flex items-center gap-3 p-2 bg-neutral-50 rounded-lg">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <CreditCard className="w-5 h-5 text-blue-700" />
                </div>
                <div>
                  <p className="font-medium text-sm">Credit Card</p>
                  <p className="text-xs text-neutral-500">3.37" × 2.13"</p>
                </div>
              </div>
              <div className="flex items-center gap-3 p-2 bg-neutral-50 rounded-lg">
                <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                  <Receipt className="w-5 h-5 text-green-700" />
                </div>
                <div>
                  <p className="font-medium text-sm">Dollar Bill</p>
                  <p className="text-xs text-neutral-500">6.14" × 2.61"</p>
                </div>
              </div>
              <div className="flex items-center gap-3 p-2 bg-neutral-50 rounded-lg">
                <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                  <FileText className="w-5 h-5 text-purple-700" />
                </div>
                <div>
                  <p className="font-medium text-sm">Letter Paper</p>
                  <p className="text-xs text-neutral-500">8.5" × 11"</p>
                </div>
              </div>
              <div className="flex items-center gap-3 p-2 bg-neutral-50 rounded-lg">
                <div className="w-10 h-10 bg-neutral-200 rounded-lg flex items-center justify-center">
                  <Smartphone className="w-5 h-5 text-neutral-700" />
                </div>
                <div>
                  <p className="font-medium text-sm">Smartphone</p>
                  <p className="text-xs text-neutral-500">~6" tall</p>
                </div>
              </div>
            </div>
          </div>

          {/* Sample Photos - Good vs Bad */}
          <div className="bg-white rounded-lg p-4 border border-blue-100">
            <h4 className="font-semibold text-blue-900 mb-3">Sample Photos: Good vs Bad</h4>
            <div className="grid grid-cols-2 gap-4">
              {/* Good Example */}
              <div className="rounded-lg overflow-hidden border-2 border-green-400">
                <div className="bg-green-100 px-3 py-1.5 flex items-center gap-2">
                  <Check className="w-4 h-4 text-green-600" />
                  <span className="font-medium text-green-800 text-sm">Good Photo</span>
                </div>
                <img 
                  src="https://static.prod-images.emergentagent.com/jobs/40af1b99-2067-4f0a-9458-756e0f39b6b0/images/c755708f5d46307dc901163edfb4ac0ff46271344b2595f15cab0001ad20a991.png"
                  alt="Good measurement photo example"
                  className="w-full h-40 object-cover"
                />
                <div className="p-3 bg-green-50 text-xs text-green-800 space-y-1">
                  <p>✓ Ruler clearly visible on windowsill</p>
                  <p>✓ Good lighting, sharp focus</p>
                  <p>✓ Full window frame captured</p>
                  <p>✓ Reference object flat & parallel</p>
                </div>
              </div>
              
              {/* Bad Example */}
              <div className="rounded-lg overflow-hidden border-2 border-red-400">
                <div className="bg-red-100 px-3 py-1.5 flex items-center gap-2">
                  <X className="w-4 h-4 text-red-600" />
                  <span className="font-medium text-red-800 text-sm">Bad Photo</span>
                </div>
                <img 
                  src="https://static.prod-images.emergentagent.com/jobs/40af1b99-2067-4f0a-9458-756e0f39b6b0/images/133c568562693e24a42f66fcbd76d2b98faf5e10ec078ae5c4b5ba16ec8fdbac.png"
                  alt="Bad measurement photo example"
                  className="w-full h-40 object-cover"
                />
                <div className="p-3 bg-red-50 text-xs text-red-800 space-y-1">
                  <p>✗ No reference object visible</p>
                  <p>✗ Dark, poor lighting</p>
                  <p>✗ Blurry, out of focus</p>
                  <p>✗ Angled perspective, cut off</p>
                </div>
              </div>
            </div>
          </div>

          {/* Pro Tips */}
          <div className="bg-amber-50 rounded-lg p-4 border border-amber-200">
            <h4 className="font-semibold text-amber-900 mb-2 flex items-center gap-2">
              <Sparkles className="w-4 h-4" /> Pro Tips
            </h4>
            <ul className="text-sm text-amber-800 space-y-1">
              <li>• A <strong>12" ruler</strong> gives the most accurate results</li>
              <li>• Take photos in <strong>good lighting</strong> conditions</li>
              <li>• For large windows, use <strong>letter paper</strong> or multiple reference points</li>
              <li>• Always <strong>verify AI measurements</strong> with a tape measure for final order</li>
            </ul>
          </div>
        </div>
      )}
    </div>
  );
};

// Tab 2: Window & Photos
const WindowPhotosTab = ({ 
  project, selectedWindow, selectedWindowId, setSelectedWindowId,
  addWindow, updateWindow, deleteWindow, token, setProject
}) => {
  const [uploading, setUploading] = useState(false);
  const [photoTag, setPhotoTag] = useState('');
  const [analyzingQuality, setAnalyzingQuality] = useState(false);
  const [qualityFeedback, setQualityFeedback] = useState(null);
  const fileInputRef = useRef(null);
  const mainPhotoRef = useRef(null);

  // Analyze photo quality using AI
  const analyzePhotoQuality = async (file, photoType = 'measurement') => {
    setAnalyzingQuality(true);
    setQualityFeedback(null);
    
    try {
      // Convert file to base64
      const base64 = await new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.readAsDataURL(file);
      });
      
      const response = await axios.post(
        `${API_URL}/api/measurement-projects/analyze-photo-quality`,
        { image_base64: base64, photo_type: photoType },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setQualityFeedback(response.data);
      return response.data;
    } catch (error) {
      console.error('Quality analysis failed:', error);
      return null;
    } finally {
      setAnalyzingQuality(false);
    }
  };

  const handleMainPhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !selectedWindowId) return;
    
    setUploading(true);
    
    // Analyze photo quality first
    const quality = await analyzePhotoQuality(file, 'measurement');
    
    const formData = new FormData();
    formData.append('file', file);
    formData.append('photo_type', 'main');
    
    try {
      const response = await axios.post(
        `${API_URL}/api/measurement-projects/${project.id}/windows/${selectedWindowId}/photos`,
        formData,
        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' } }
      );
      
      setProject(prev => ({
        ...prev,
        windows: prev.windows.map(w => 
          w.id === selectedWindowId ? { ...w, main_photo_url: response.data.url, photo_quality: quality } : w
        )
      }));
      
      // Show quality-based feedback
      if (quality) {
        if (quality.quality_score >= 80) {
          toast.success(`Great photo! Quality score: ${quality.quality_score}/100`);
        } else if (quality.quality_score >= 60) {
          toast.warning(`Photo uploaded. Quality score: ${quality.quality_score}/100 - See recommendations below`);
        } else {
          toast.error(`Photo may have issues. Quality score: ${quality.quality_score}/100 - Consider retaking`);
        }
      } else {
        toast.success('Main photo uploaded');
      }
    } catch (error) {
      toast.error('Failed to upload photo');
    }
    setUploading(false);
  };

  const handleAdditionalPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !selectedWindowId) return;
    
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('photo_type', 'additional');
    formData.append('tag', photoTag);
    
    try {
      const response = await axios.post(
        `${API_URL}/api/measurement-projects/${project.id}/windows/${selectedWindowId}/photos`,
        formData,
        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' } }
      );
      
      setProject(prev => ({
        ...prev,
        windows: prev.windows.map(w => 
          w.id === selectedWindowId ? { 
            ...w, 
            additional_photos: [...(w.additional_photos || []), { 
              id: response.data.id || Date.now().toString(), 
              url: response.data.url,
              tag: photoTag
            }] 
          } : w
        )
      }));
      setPhotoTag('');
      toast.success('Photo added');
    } catch (error) {
      toast.error('Failed to upload photo');
    }
    setUploading(false);
  };

  return (
    <div>
      {/* Photo Guide */}
      <PhotoGuide />
      
      <div className="grid grid-cols-3 gap-6">
      {/* Window List */}
      <div className="col-span-1 border-r pr-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold">Windows</h3>
          <Button size="sm" onClick={addWindow} data-testid="add-window-btn">
            <Plus className="w-4 h-4 mr-1" /> Add
          </Button>
        </div>
        
        {(project.windows || []).length === 0 ? (
          <div className="text-center py-8 text-neutral-500">
            <Camera className="w-8 h-8 mx-auto mb-2 text-neutral-300" />
            <p className="text-sm">No windows yet</p>
            <Button size="sm" variant="outline" className="mt-2" onClick={addWindow}>
              Add First Window
            </Button>
          </div>
        ) : (
          <div className="space-y-2">
            {project.windows.map(window => (
              <div
                key={window.id}
                onClick={() => setSelectedWindowId(window.id)}
                className={`p-3 rounded-lg cursor-pointer transition-colors ${
                  selectedWindowId === window.id
                    ? 'bg-amber-50 border-amber-500 border'
                    : 'bg-neutral-50 hover:bg-neutral-100 border border-transparent'
                }`}
              >
                <div className="flex items-center justify-between">
                  <span className="font-medium text-sm">{window.window_name}</span>
                  <Button 
                    size="sm" 
                    variant="ghost" 
                    className="h-6 w-6 p-0 text-red-500"
                    onClick={(e) => { e.stopPropagation(); deleteWindow(window.id); }}
                  >
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>
                {window.main_photo_url && (
                  <div className="flex items-center gap-1 mt-1 text-xs text-green-600">
                    <Check className="w-3 h-3" /> Photo taken
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Photo Section */}
      <div className="col-span-2">
        {!selectedWindow ? (
          <div className="flex items-center justify-center h-64 text-neutral-500">
            Select a window to add photos
          </div>
        ) : (
          <div className="space-y-6">
            {/* Window Name */}
            <div>
              <Label>Window Name/ID</Label>
              <Input
                value={selectedWindow.window_name || ''}
                onChange={(e) => updateWindow(selectedWindowId, { window_name: e.target.value })}
                placeholder="e.g., Living Room - Window 1"
              />
            </div>

            {/* Main Photo */}
            <div>
              <Label>Main Measurement Photo</Label>
              <p className="text-xs text-neutral-500 mb-2">
                Include a reference object (ruler, credit card) in the frame for AI measurement
              </p>
              <div className="border-2 border-dashed rounded-lg p-4">
                {selectedWindow.main_photo_url ? (
                  <div className="relative">
                    <img
                      src={selectedWindow.main_photo_url.startsWith('/') 
                        ? `${API_URL}${selectedWindow.main_photo_url}` 
                        : selectedWindow.main_photo_url}
                      alt="Main measurement"
                      className="w-full max-h-64 object-contain rounded"
                    />
                    <Button 
                      size="sm" 
                      variant="outline"
                      className="absolute top-2 right-2"
                      onClick={() => mainPhotoRef.current?.click()}
                    >
                      <Camera className="w-4 h-4 mr-1" /> Replace
                    </Button>
                  </div>
                ) : (
                  <div 
                    className="flex flex-col items-center justify-center py-8 cursor-pointer"
                    onClick={() => mainPhotoRef.current?.click()}
                  >
                    <Camera className="w-12 h-12 text-neutral-300 mb-2" />
                    <p className="text-sm text-neutral-500">Click to take/upload main photo</p>
                  </div>
                )}
                <input
                  ref={mainPhotoRef}
                  type="file"
                  accept="image/*"
                  capture="environment"
                  onChange={handleMainPhotoUpload}
                  className="hidden"
                />
              </div>
            </div>

            {/* Photo Quality Feedback Panel */}
            {(analyzingQuality || qualityFeedback) && (
              <div className={`rounded-lg border-2 p-4 ${
                analyzingQuality ? 'border-blue-200 bg-blue-50' :
                qualityFeedback?.quality_score >= 80 ? 'border-green-200 bg-green-50' :
                qualityFeedback?.quality_score >= 60 ? 'border-amber-200 bg-amber-50' :
                'border-red-200 bg-red-50'
              }`} data-testid="photo-quality-feedback">
                {analyzingQuality ? (
                  <div className="flex items-center gap-3">
                    <Loader2 className="w-5 h-5 animate-spin text-blue-600" />
                    <div>
                      <p className="font-medium text-blue-900">Analyzing Photo Quality...</p>
                      <p className="text-sm text-blue-700">Checking clarity, lighting, and reference object</p>
                    </div>
                  </div>
                ) : qualityFeedback && (
                  <div className="space-y-3">
                    {/* Quality Score Header */}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        {qualityFeedback.quality_score >= 80 ? (
                          <Check className="w-5 h-5 text-green-600" />
                        ) : qualityFeedback.quality_score >= 60 ? (
                          <AlertCircle className="w-5 h-5 text-amber-600" />
                        ) : (
                          <X className="w-5 h-5 text-red-600" />
                        )}
                        <span className={`font-semibold ${
                          qualityFeedback.quality_score >= 80 ? 'text-green-800' :
                          qualityFeedback.quality_score >= 60 ? 'text-amber-800' : 'text-red-800'
                        }`}>
                          Photo Quality: {qualityFeedback.quality_score}/100
                        </span>
                      </div>
                      <Button 
                        size="sm" 
                        variant="ghost"
                        onClick={() => setQualityFeedback(null)}
                        className="h-6 px-2"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>

                    {/* Reference Object Status */}
                    <div className="flex items-center gap-4 text-sm">
                      <div className={`flex items-center gap-1 ${qualityFeedback.has_reference_object ? 'text-green-700' : 'text-red-700'}`}>
                        {qualityFeedback.has_reference_object ? <Check className="w-4 h-4" /> : <X className="w-4 h-4" />}
                        Reference object {qualityFeedback.has_reference_object ? 'detected' : 'not found'}
                      </div>
                      {qualityFeedback.has_reference_object && (
                        <div className={`flex items-center gap-1 ${qualityFeedback.reference_object_visible ? 'text-green-700' : 'text-amber-700'}`}>
                          {qualityFeedback.reference_object_visible ? <Check className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
                          {qualityFeedback.reference_object_visible ? 'Clearly visible' : 'Partially visible'}
                        </div>
                      )}
                    </div>

                    {/* Summary */}
                    <p className="text-sm text-neutral-700">{qualityFeedback.summary}</p>

                    {/* Issues */}
                    {qualityFeedback.issues?.length > 0 && (
                      <div className="space-y-2">
                        <p className="text-sm font-medium text-neutral-800">Issues Found:</p>
                        {qualityFeedback.issues.map((issue, idx) => (
                          <div 
                            key={idx} 
                            className={`flex items-start gap-2 text-sm p-2 rounded ${
                              issue.severity === 'critical' ? 'bg-red-100 text-red-800' :
                              issue.severity === 'warning' ? 'bg-amber-100 text-amber-800' :
                              'bg-blue-100 text-blue-800'
                            }`}
                          >
                            {issue.severity === 'critical' ? (
                              <X className="w-4 h-4 mt-0.5 flex-shrink-0" />
                            ) : issue.severity === 'warning' ? (
                              <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                            ) : (
                              <Info className="w-4 h-4 mt-0.5 flex-shrink-0" />
                            )}
                            <div>
                              <p className="font-medium">{issue.message}</p>
                              <p className="text-xs opacity-80">Tip: {issue.suggestion}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Recommendations */}
                    {qualityFeedback.recommendations?.length > 0 && (
                      <div className="mt-2">
                        <p className="text-sm font-medium text-neutral-800 mb-1">Recommendations:</p>
                        <ul className="text-sm text-neutral-700 space-y-1">
                          {qualityFeedback.recommendations.map((rec, idx) => (
                            <li key={idx} className="flex items-start gap-2">
                              <Sparkles className="w-4 h-4 mt-0.5 text-amber-500 flex-shrink-0" />
                              {rec}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* Retake Button for Poor Quality */}
                    {qualityFeedback.quality_score < 60 && (
                      <Button 
                        variant="outline" 
                        size="sm"
                        className="mt-2 border-red-300 text-red-700 hover:bg-red-100"
                        onClick={() => mainPhotoRef.current?.click()}
                      >
                        <Camera className="w-4 h-4 mr-2" />
                        Retake Photo
                      </Button>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Additional Photos */}
            <div>
              <Label>Additional Photos</Label>
              <p className="text-xs text-neutral-500 mb-2">Add close-ups, angles, or detail shots</p>
              
              <div className="flex gap-2 mb-3">
                <select
                  value={photoTag}
                  onChange={(e) => setPhotoTag(e.target.value)}
                  className="h-9 px-3 border rounded-md bg-white text-sm"
                >
                  <option value="">Select tag...</option>
                  <option value="left-frame">Left Frame</option>
                  <option value="right-frame">Right Frame</option>
                  <option value="top-detail">Top Detail</option>
                  <option value="bottom-detail">Bottom Detail</option>
                  <option value="hardware">Hardware</option>
                  <option value="obstruction">Obstruction</option>
                  <option value="full-view">Full View</option>
                </select>
                <label>
                  <Button variant="outline" size="sm" disabled={uploading} asChild>
                    <span>
                      {uploading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Camera className="w-4 h-4 mr-1" />}
                      Add Photo
                    </span>
                  </Button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    capture="environment"
                    onChange={handleAdditionalPhoto}
                    className="hidden"
                  />
                </label>
              </div>

              <div className="grid grid-cols-4 gap-2">
                {(selectedWindow.additional_photos || []).map((photo, idx) => (
                  <div key={photo.id || idx} className="relative group">
                    <img
                      src={photo.url.startsWith('/') ? `${API_URL}${photo.url}` : photo.url}
                      alt={photo.tag || `Photo ${idx + 1}`}
                      className="w-full h-20 object-cover rounded border"
                    />
                    {photo.tag && (
                      <span className="absolute bottom-1 left-1 bg-black/60 text-white text-xs px-1 rounded">
                        {photo.tag}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
    </div>
  );
};

// Tab 3: Measurements
const MeasurementsTab = ({ project, selectedWindow, updateWindow, token, setProject }) => {
  const [analyzing, setAnalyzing] = useState(false);
  const [referenceObject, setReferenceObject] = useState('ruler');
  const [manualWidth, setManualWidth] = useState(selectedWindow?.confirmed_measurements?.width || '');
  const [manualHeight, setManualHeight] = useState(selectedWindow?.confirmed_measurements?.height || '');

  useEffect(() => {
    if (selectedWindow?.confirmed_measurements) {
      setManualWidth(selectedWindow.confirmed_measurements.width || '');
      setManualHeight(selectedWindow.confirmed_measurements.height || '');
    } else if (selectedWindow?.ai_measurements) {
      setManualWidth(selectedWindow.ai_measurements.width || '');
      setManualHeight(selectedWindow.ai_measurements.height || '');
    }
  }, [selectedWindow]);

  const analyzePhoto = async () => {
    if (!selectedWindow?.main_photo_url) {
      toast.error('Please upload a main photo first');
      return;
    }
    
    setAnalyzing(true);
    try {
      const formData = new FormData();
      formData.append('reference_object', referenceObject);
      
      const response = await axios.post(
        `${API_URL}/api/measurement-projects/${project.id}/windows/${selectedWindow.id}/analyze-photo`,
        formData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const aiMeasurements = response.data.ai_measurements;
      setProject(prev => ({
        ...prev,
        windows: prev.windows.map(w => 
          w.id === selectedWindow.id ? { ...w, ai_measurements: aiMeasurements, reference_object: referenceObject } : w
        )
      }));
      
      // Pre-fill manual fields with AI suggestions
      if (aiMeasurements) {
        setManualWidth(aiMeasurements.width || '');
        setManualHeight(aiMeasurements.height || '');
      }
      
      toast.success(`AI estimated: ${aiMeasurements.width}" x ${aiMeasurements.height}" (${aiMeasurements.confidence} confidence)`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Analysis failed');
    }
    setAnalyzing(false);
  };

  const saveConfirmedMeasurements = () => {
    if (!selectedWindow) return;
    updateWindow(selectedWindow.id, {
      confirmed_measurements: {
        width: parseFloat(manualWidth) || null,
        height: parseFloat(manualHeight) || null,
        unit: 'inches'
      }
    });
    toast.success('Measurements saved');
  };

  if (!selectedWindow) {
    return (
      <div className="flex items-center justify-center h-64 text-neutral-500">
        Select a window from the "Window & Photos" tab first
      </div>
    );
  }

  return (
    <div className="max-w-2xl space-y-6">
      {/* AI Analysis Section */}
      <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
        <h3 className="font-semibold flex items-center gap-2 mb-3">
          <Sparkles className="w-5 h-5 text-purple-600" />
          AI Measurement Analysis
        </h3>
        
        {!selectedWindow.main_photo_url ? (
          <p className="text-sm text-purple-700">
            Upload a main photo with a reference object to use AI analysis
          </p>
        ) : (
          <>
            <div className="flex items-center gap-3 mb-3">
              <Label className="whitespace-nowrap">Reference Object:</Label>
              <select
                value={referenceObject}
                onChange={(e) => setReferenceObject(e.target.value)}
                className="h-9 px-3 border rounded-md bg-white text-sm"
              >
                <option value="ruler">12" Ruler</option>
                <option value="credit_card">Credit Card (3.37" x 2.125")</option>
                <option value="dollar_bill">Dollar Bill (6.14" x 2.61")</option>
                <option value="letter_paper">Letter Paper (8.5" x 11")</option>
                <option value="smartphone">Smartphone (~6" tall)</option>
              </select>
              
              <Button 
                onClick={analyzePhoto} 
                disabled={analyzing}
                className="bg-purple-600 hover:bg-purple-700"
              >
                {analyzing ? (
                  <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Analyzing...</>
                ) : (
                  <><Sparkles className="w-4 h-4 mr-2" /> Analyze Photo</>
                )}
              </Button>
            </div>
            
            {selectedWindow.ai_measurements && (
              <div className="mt-3 p-3 bg-white rounded border">
                <p className="text-sm font-medium">AI Estimated Dimensions:</p>
                <p className="text-lg">
                  {selectedWindow.ai_measurements.width}" W × {selectedWindow.ai_measurements.height}" H
                </p>
                <p className="text-xs text-neutral-500">
                  Confidence: {selectedWindow.ai_measurements.confidence}
                  {selectedWindow.ai_measurements.notes && ` • ${selectedWindow.ai_measurements.notes}`}
                </p>
              </div>
            )}
          </>
        )}
      </div>

      {/* Manual Entry */}
      <div>
        <h3 className="font-semibold mb-4">Confirmed Measurements</h3>
        <p className="text-sm text-neutral-500 mb-3">
          Enter or confirm the exact measurements
        </p>
        
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label>Width (inches)</Label>
            <Input
              type="number"
              step="0.125"
              value={manualWidth}
              onChange={(e) => setManualWidth(e.target.value)}
              placeholder="e.g., 48.5"
            />
          </div>
          <div>
            <Label>Height (inches)</Label>
            <Input
              type="number"
              step="0.125"
              value={manualHeight}
              onChange={(e) => setManualHeight(e.target.value)}
              placeholder="e.g., 72"
            />
          </div>
        </div>
        
        <Button onClick={saveConfirmedMeasurements} className="mt-4">
          <Check className="w-4 h-4 mr-2" /> Confirm Measurements
        </Button>
      </div>

      {/* Voice Notes Section */}
      <VoiceNotesSection
        project={project}
        selectedWindow={selectedWindow}
        token={token}
        setProject={setProject}
        step="measurements"
      />

      {/* Manual Notes */}
      <div>
        <Label>Additional Notes</Label>
        <Textarea
          value={selectedWindow.manual_notes || ''}
          onChange={(e) => updateWindow(selectedWindow.id, { manual_notes: e.target.value })}
          placeholder="Add any notes about this window..."
          rows={3}
        />
      </div>
    </div>
  );
};

// Voice Notes Component
const VoiceNotesSection = ({ project, selectedWindow, token, setProject, step }) => {
  const [recording, setRecording] = useState(false);
  const [uploading, setUploading] = useState(false);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      chunksRef.current = [];
      
      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunksRef.current.push(e.data);
        }
      };
      
      mediaRecorder.onstop = async () => {
        const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
        stream.getTracks().forEach(track => track.stop());
        await uploadVoiceNote(blob);
      };
      
      mediaRecorder.start();
      setRecording(true);
    } catch (error) {
      toast.error('Microphone access denied');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && recording) {
      mediaRecorderRef.current.stop();
      setRecording(false);
    }
  };

  const uploadVoiceNote = async (blob) => {
    setUploading(true);
    const formData = new FormData();
    formData.append('file', blob, 'voice_note.webm');
    formData.append('step', step);
    
    try {
      const response = await axios.post(
        `${API_URL}/api/measurement-projects/${project.id}/windows/${selectedWindow.id}/voice-notes`,
        formData,
        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' } }
      );
      
      setProject(prev => ({
        ...prev,
        windows: prev.windows.map(w => 
          w.id === selectedWindow.id ? { 
            ...w, 
            voice_notes: [...(w.voice_notes || []), response.data] 
          } : w
        )
      }));
      
      toast.success(response.data.transcription 
        ? `Voice note transcribed: "${response.data.transcription.substring(0, 50)}..."` 
        : 'Voice note saved');
    } catch (error) {
      toast.error('Failed to save voice note');
    }
    setUploading(false);
  };

  return (
    <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
      <h4 className="font-medium flex items-center gap-2 mb-3">
        <Mic className="w-4 h-4 text-blue-600" />
        Voice Notes
      </h4>
      
      <div className="flex items-center gap-3 mb-3">
        {!recording ? (
          <Button 
            onClick={startRecording} 
            variant="outline"
            disabled={uploading}
            className="border-blue-300"
          >
            <Mic className="w-4 h-4 mr-2 text-blue-600" /> Record Note
          </Button>
        ) : (
          <Button 
            onClick={stopRecording}
            className="bg-red-500 hover:bg-red-600 animate-pulse"
          >
            <MicOff className="w-4 h-4 mr-2" /> Stop Recording
          </Button>
        )}
        
        {uploading && (
          <span className="text-sm text-neutral-500 flex items-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" /> Transcribing...
          </span>
        )}
      </div>
      
      {/* Voice Notes List */}
      {(selectedWindow?.voice_notes || []).filter(n => n.step === step).length > 0 && (
        <div className="space-y-2 mt-3">
          {selectedWindow.voice_notes.filter(n => n.step === step).map((note, idx) => (
            <div key={note.id || idx} className="p-2 bg-white rounded border text-sm">
              <div className="flex items-center gap-2 mb-1">
                <Volume2 className="w-3 h-3 text-blue-500" />
                <audio src={note.url.startsWith('/') ? `${API_URL}${note.url}` : note.url} controls className="h-6" />
              </div>
              {note.transcription && (
                <p className="text-neutral-700 italic">"{note.transcription}"</p>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Tab 4: Installation Details
const InstallationTab = ({ selectedWindow, updateWindow, options }) => {
  if (!selectedWindow) {
    return (
      <div className="flex items-center justify-center h-64 text-neutral-500">
        Select a window first
      </div>
    );
  }

  return (
    <div className="max-w-2xl space-y-6">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>Mount Type</Label>
          <select
            value={selectedWindow.mount_type || ''}
            onChange={(e) => updateWindow(selectedWindow.id, { mount_type: e.target.value })}
            className="w-full h-10 px-3 border rounded-md bg-white"
          >
            <option value="">Select mount type...</option>
            {(options.mount_types || []).map(type => (
              <option key={type} value={type}>{type}</option>
            ))}
          </select>
        </div>
        <div>
          <Label>Window Type</Label>
          <select
            value={selectedWindow.window_type || ''}
            onChange={(e) => updateWindow(selectedWindow.id, { window_type: e.target.value })}
            className="w-full h-10 px-3 border rounded-md bg-white"
          >
            <option value="">Select window type...</option>
            {(options.window_types || []).map(type => (
              <option key={type} value={type}>{type}</option>
            ))}
          </select>
        </div>
      </div>

      <div>
        <Label>Frame Material</Label>
        <select
          value={selectedWindow.frame_material || ''}
          onChange={(e) => updateWindow(selectedWindow.id, { frame_material: e.target.value })}
          className="w-full h-10 px-3 border rounded-md bg-white"
        >
          <option value="">Select frame material...</option>
          {(options.frame_materials || []).map(mat => (
            <option key={mat} value={mat}>{mat}</option>
          ))}
        </select>
      </div>

      <div>
        <Label>Special Features</Label>
        <div className="grid grid-cols-2 gap-2 mt-2">
          {(options.special_features || []).map(feature => (
            <label key={feature} className="flex items-center gap-2 p-2 border rounded hover:bg-neutral-50 cursor-pointer">
              <input
                type="checkbox"
                checked={(selectedWindow.special_features || []).includes(feature)}
                onChange={(e) => {
                  const current = selectedWindow.special_features || [];
                  const updated = e.target.checked 
                    ? [...current, feature]
                    : current.filter(f => f !== feature);
                  updateWindow(selectedWindow.id, { special_features: updated });
                }}
                className="rounded"
              />
              <span className="text-sm">{feature}</span>
            </label>
          ))}
        </div>
      </div>
    </div>
  );
};

// Tab 5: Product Selection
const ProductTab = ({ selectedWindow, updateWindow, options }) => {
  if (!selectedWindow) {
    return (
      <div className="flex items-center justify-center h-64 text-neutral-500">
        Select a window first
      </div>
    );
  }

  return (
    <div className="max-w-2xl space-y-6">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>Fabric Type</Label>
          <select
            value={selectedWindow.fabric_type || ''}
            onChange={(e) => updateWindow(selectedWindow.id, { fabric_type: e.target.value })}
            className="w-full h-10 px-3 border rounded-md bg-white"
          >
            <option value="">Select fabric type...</option>
            {(options.fabric_types || []).map(type => (
              <option key={type} value={type}>{type}</option>
            ))}
          </select>
        </div>
        <div>
          <Label>Fabric Color</Label>
          <Input
            value={selectedWindow.fabric_color || ''}
            onChange={(e) => updateWindow(selectedWindow.id, { fabric_color: e.target.value })}
            placeholder="e.g., White, Beige, Gray"
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>Control Type</Label>
          <select
            value={selectedWindow.control_type || ''}
            onChange={(e) => updateWindow(selectedWindow.id, { control_type: e.target.value })}
            className="w-full h-10 px-3 border rounded-md bg-white"
          >
            <option value="">Select control type...</option>
            {(options.control_types || []).map(type => (
              <option key={type} value={type}>{type}</option>
            ))}
          </select>
        </div>
        <div>
          <Label>Roll Direction</Label>
          <select
            value={selectedWindow.roll_direction || ''}
            onChange={(e) => updateWindow(selectedWindow.id, { roll_direction: e.target.value })}
            className="w-full h-10 px-3 border rounded-md bg-white"
          >
            <option value="">Select roll direction...</option>
            {(options.roll_directions || []).map(dir => (
              <option key={dir} value={dir}>{dir}</option>
            ))}
          </select>
        </div>
      </div>
    </div>
  );
};

// Tab 6: Fascia
const FasciaTab = ({ selectedWindow, updateWindow, options }) => {
  if (!selectedWindow) {
    return (
      <div className="flex items-center justify-center h-64 text-neutral-500">
        Select a window first
      </div>
    );
  }

  return (
    <div className="max-w-2xl space-y-6">
      <div>
        <Label>Include Fascia?</Label>
        <div className="flex gap-4 mt-2">
          <label className="flex items-center gap-2 p-3 border rounded-lg cursor-pointer hover:bg-neutral-50">
            <input
              type="radio"
              name="hasFascia"
              checked={selectedWindow.has_fascia === true}
              onChange={() => updateWindow(selectedWindow.id, { has_fascia: true })}
            />
            <span>Yes</span>
          </label>
          <label className="flex items-center gap-2 p-3 border rounded-lg cursor-pointer hover:bg-neutral-50">
            <input
              type="radio"
              name="hasFascia"
              checked={selectedWindow.has_fascia !== true}
              onChange={() => updateWindow(selectedWindow.id, { has_fascia: false })}
            />
            <span>No</span>
          </label>
        </div>
      </div>

      {selectedWindow.has_fascia && (
        <>
          <div>
            <Label>Fascia Style</Label>
            <select
              value={selectedWindow.fascia_style || ''}
              onChange={(e) => updateWindow(selectedWindow.id, { fascia_style: e.target.value })}
              className="w-full h-10 px-3 border rounded-md bg-white"
            >
              <option value="">Select fascia style...</option>
              {(options.fascia_styles || []).map(style => (
                <option key={style} value={style}>{style}</option>
              ))}
            </select>
          </div>
          <div>
            <Label>Fascia Color</Label>
            <Input
              value={selectedWindow.fascia_color || ''}
              onChange={(e) => updateWindow(selectedWindow.id, { fascia_color: e.target.value })}
              placeholder="e.g., White, Match Fabric, Custom"
            />
          </div>
        </>
      )}
    </div>
  );
};

// Tab 7: Review
const ReviewTab = ({ project, updateProject, token, onProjectLinked }) => {
  const [submitting, setSubmitting] = useState(false);
  const [showSubmitDialog, setShowSubmitDialog] = useState(false);
  const [estimatedInstallDate, setEstimatedInstallDate] = useState(project.estimated_installation_date || '');
  const [installers, setInstallers] = useState([]);
  const [selectedInstaller, setSelectedInstaller] = useState(project.assigned_installer_id || '');
  const [submitNotes, setSubmitNotes] = useState('');

  // Fetch installers for assignment dropdown
  useEffect(() => {
    const fetchInstallers = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/admin/installers`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setInstallers(response.data || []);
      } catch (error) {
        console.error('Failed to fetch installers:', error);
      }
    };
    fetchInstallers();
  }, [token]);

  const handleSubmitToProjects = async () => {
    setSubmitting(true);
    try {
      // Save estimated installation date first
      if (estimatedInstallDate) {
        await updateProject({ estimated_installation_date: estimatedInstallDate });
      }

      // Link to main projects dashboard
      const response = await axios.post(
        `${API_URL}/api/measurement-projects/${project.id}/link-to-project`,
        {
          create_new: true,
          estimated_installation_date: estimatedInstallDate,
          assigned_installer_id: selectedInstaller || null,
          notes: submitNotes
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.already_linked) {
        toast.info(`Already linked to project ${response.data.project_number || response.data.project_id}`);
      } else {
        toast.success(`Project ${response.data.project_number} created in dashboard!`);
        if (onProjectLinked) {
          onProjectLinked(response.data.project_id, response.data.project_number);
        }
      }
      setShowSubmitDialog(false);
    } catch (error) {
      toast.error('Failed to submit to projects');
    }
    setSubmitting(false);
  };

  return (
    <div className="max-w-3xl">
      <h3 className="text-lg font-semibold mb-4">Project Summary</h3>
      
      {/* Created By Info */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
        <div className="flex items-center gap-2 mb-2">
          <User className="w-4 h-4 text-blue-600" />
          <span className="font-medium text-blue-900">Created By</span>
        </div>
        <div className="grid grid-cols-2 gap-2 text-sm">
          <div><span className="text-blue-600">Name:</span> {project.created_by_name || 'Unknown'}</div>
          <div><span className="text-blue-600">Role:</span> {project.created_by_role || 'N/A'}</div>
          <div><span className="text-blue-600">Date:</span> {project.created_at ? new Date(project.created_at).toLocaleDateString() : 'N/A'}</div>
          <div><span className="text-blue-600">Status:</span> 
            <span className={`ml-1 px-2 py-0.5 rounded text-xs font-medium ${
              project.status === 'draft' ? 'bg-neutral-200 text-neutral-700' :
              project.status === 'in_progress' ? 'bg-blue-200 text-blue-700' :
              project.status === 'completed' ? 'bg-green-200 text-green-700' :
              project.status === 'submitted' ? 'bg-purple-200 text-purple-700' : ''
            }`}>
              {project.status || 'draft'}
            </span>
          </div>
        </div>
      </div>

      {/* Linked Project Banner */}
      {project.linked_project_id && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Check className="w-5 h-5 text-green-600" />
            <div>
              <p className="font-medium text-green-800">Submitted to Projects Dashboard</p>
              <p className="text-sm text-green-600">Project: {project.linked_project_number || project.linked_project_id}</p>
            </div>
          </div>
        </div>
      )}

      {/* Project Info */}
      <div className="bg-neutral-50 rounded-lg p-4 mb-4">
        <h4 className="font-medium mb-2">Project Information</h4>
        <div className="grid grid-cols-2 gap-2 text-sm">
          <div><span className="text-neutral-500">Name:</span> {project.project_name}</div>
          <div><span className="text-neutral-500">Room:</span> {project.custom_room_name || project.room_type}</div>
          <div><span className="text-neutral-500">Customer:</span> {project.customer_name || 'N/A'}</div>
          <div><span className="text-neutral-500">Address:</span> {project.customer_address || 'N/A'}</div>
          <div><span className="text-neutral-500">Phone:</span> {project.customer_phone || 'N/A'}</div>
          <div><span className="text-neutral-500">Email:</span> {project.customer_email || 'N/A'}</div>
        </div>
      </div>

      {/* Estimated Installation Date */}
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
        <Label className="flex items-center gap-2 mb-2">
          <Calendar className="w-4 h-4 text-amber-600" />
          Estimated Installation Date
        </Label>
        <Input
          type="date"
          value={estimatedInstallDate}
          onChange={(e) => {
            setEstimatedInstallDate(e.target.value);
            updateProject({ estimated_installation_date: e.target.value });
          }}
          className="max-w-xs"
        />
        <p className="text-xs text-amber-600 mt-1">Set an estimated date for the installation</p>
      </div>

      {/* Installer Assignment (for admins) */}
      {installers.length > 0 && (
        <div className="bg-neutral-50 rounded-lg p-4 mb-4">
          <Label className="flex items-center gap-2 mb-2">
            <HardHat className="w-4 h-4 text-neutral-600" />
            Assigned Installer
          </Label>
          <select
            value={selectedInstaller}
            onChange={(e) => {
              setSelectedInstaller(e.target.value);
              const installer = installers.find(i => i.id === e.target.value);
              updateProject({ 
                assigned_installer_id: e.target.value,
                assigned_installer_name: installer?.name || ''
              });
            }}
            className="w-full max-w-xs h-10 px-3 border rounded-md bg-white"
          >
            <option value="">Not assigned</option>
            {installers.map(installer => (
              <option key={installer.id} value={installer.id}>{installer.name}</option>
            ))}
          </select>
        </div>
      )}

      {/* Windows Summary */}
      <div className="space-y-4">
        <h4 className="font-medium">Windows ({project.windows?.length || 0})</h4>
        
        {(project.windows || []).map((window, idx) => (
          <div key={window.id} className="border rounded-lg p-4">
            <div className="flex items-center justify-between mb-3">
              <h5 className="font-medium">{window.window_name}</h5>
              {window.confirmed_measurements?.width && window.confirmed_measurements?.height && (
                <span className="bg-green-100 text-green-700 px-2 py-1 rounded text-sm">
                  {window.confirmed_measurements.width}" × {window.confirmed_measurements.height}"
                </span>
              )}
            </div>
            
            <div className="grid grid-cols-3 gap-4 text-sm">
              <div>
                <span className="text-neutral-500">Mount:</span><br />
                {window.mount_type || 'Not set'}
              </div>
              <div>
                <span className="text-neutral-500">Fabric:</span><br />
                {window.fabric_type || 'Not set'} {window.fabric_color && `- ${window.fabric_color}`}
              </div>
              <div>
                <span className="text-neutral-500">Control:</span><br />
                {window.control_type || 'Not set'}
              </div>
            </div>
            
            {window.main_photo_url && (
              <div className="mt-3">
                <img
                  src={window.main_photo_url.startsWith('/') 
                    ? `${API_URL}${window.main_photo_url}` 
                    : window.main_photo_url}
                  alt="Window"
                  className="h-20 object-cover rounded"
                />
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Status Update */}
      <div className="mt-6 p-4 bg-neutral-50 rounded-lg border">
        <Label>Project Status</Label>
        <select
          value={project.status || 'draft'}
          onChange={(e) => updateProject({ status: e.target.value })}
          className="w-full h-10 px-3 border rounded-md bg-white mt-2"
        >
          <option value="draft">Draft</option>
          <option value="in_progress">In Progress</option>
          <option value="completed">Completed</option>
          <option value="submitted">Submitted</option>
        </select>
      </div>

      {/* Submit to Projects Button */}
      {!project.linked_project_id && (
        <div className="mt-6 p-4 bg-green-50 rounded-lg border border-green-200">
          <h4 className="font-medium text-green-800 mb-2 flex items-center gap-2">
            <FolderOpen className="w-4 h-4" />
            Submit to Projects Dashboard
          </h4>
          <p className="text-sm text-green-700 mb-4">
            Create a project in the main dashboard to track quotes, production, and installation.
          </p>
          <Button 
            onClick={() => setShowSubmitDialog(true)}
            className="bg-green-600 hover:bg-green-700"
            data-testid="submit-to-projects-btn"
          >
            <ChevronRight className="w-4 h-4 mr-2" />
            Submit to Projects
          </Button>
        </div>
      )}

      {/* Submit Dialog */}
      <Dialog open={showSubmitDialog} onOpenChange={setShowSubmitDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Submit to Projects Dashboard</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-neutral-600">
              This will create a new project in the main dashboard with all measurement data.
              The project will be visible to admins and can be assigned to installers.
            </p>
            
            <div>
              <Label>Estimated Installation Date</Label>
              <Input
                type="date"
                value={estimatedInstallDate}
                onChange={(e) => setEstimatedInstallDate(e.target.value)}
                className="mt-1"
              />
            </div>

            {installers.length > 0 && (
              <div>
                <Label>Assign Installer (Optional)</Label>
                <select
                  value={selectedInstaller}
                  onChange={(e) => setSelectedInstaller(e.target.value)}
                  className="w-full h-10 px-3 border rounded-md bg-white mt-1"
                >
                  <option value="">Not assigned</option>
                  {installers.map(installer => (
                    <option key={installer.id} value={installer.id}>{installer.name}</option>
                  ))}
                </select>
              </div>
            )}

            <div>
              <Label>Notes (Optional)</Label>
              <Textarea
                value={submitNotes}
                onChange={(e) => setSubmitNotes(e.target.value)}
                placeholder="Add any notes for the project..."
                rows={3}
              />
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => setShowSubmitDialog(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleSubmitToProjects}
                disabled={submitting}
                className="bg-green-600 hover:bg-green-700"
              >
                {submitting ? (
                  <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Creating...</>
                ) : (
                  'Create Project'
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MeasurementProjectsTab;
